import { get } from 'lodash';
import { resourceStatus, DEFAULT_ID, resourceError } from 'constants/loading';

const getResourceByStatus = ({ state, type, id=DEFAULT_ID, status }) => {
  const resource = get(state, `loading[${status}][${type}][${id}]`);
  return (resource && resource.status) ? resource : null;
};


const activeResources = resources => {
  if(!resources) {
    return {};
  }
  const active = Object.entries(resources).reduce( (all, [id, resource]) => {
    // Filter out empty/invalid entries
    const hasContent = !!Object.keys(resource).length;
    const isActive = !!resource.status;
    if( hasContent && isActive ) {
      all[id] = resource;
    }
    return all;
  }, {});
  return active;
};

const hasActiveResources = resources => {
  if(!resources) {
    return false;
  }
  const filtered = activeResources(resources);
  return !!Object.keys(filtered).length;
};

const getResourceTypeByStatus = ({ state, type, status }) => {
  const matching = get(state, `loading[${status}][${type}]`);
  if( !hasActiveResources(matching) ) {
    return {};
  }
  const active = activeResources(matching);
  return active;
};

// The type+id is not LOADED nor PENDING
export const isResourceUnloaded = ({ state, type, id }) => {
  const loaded = getResourceByStatus({ state, type, id, status: resourceStatus.LOADED });
  const pending = getResourceByStatus({ state, type, id, status: resourceStatus.PENDING });
  return !( loaded || pending );
};

export const isResourceLoaded = ({ state, type, id }) => {
  return !!getResourceByStatus({ state, type, id, status: resourceStatus.LOADED });
};

export const isResourcePending = ({ state, type, id }) => {
  // finders check to see if data is still needed/wanted
  return !!getResourceByStatus({ state, type, id, status: resourceStatus.PENDING });
};

const getPendingResourceTypePromise = ({ state, type }) => {
  const resources = getResourceTypeByStatus({ state, type, status: resourceStatus.PENDING });
  const filtered = Object.values(resources).filter( resource => resource.promise );
  const promiseCount = filtered.length;
  if(promiseCount === 1) {
    return filtered[0].promise;
  }
  if(promiseCount > 1) {
    return Promise.reject(resourceError.CONFLICT);
  }
  return Promise.reject(resourceError.EMPTY);
};

export const getPendingResourcePromise = ({ state, type, id }) => {
  if( !id ) {
    return getPendingResourceTypePromise({ state, type });
  }
  const resource = getResourceByStatus({ state, type, id, status: resourceStatus.PENDING });
  return resource && resource.promise;
};

// Useful to use when polling to check if still needed
export const createResourceNotPendingCheck = ({ getState, type, id=DEFAULT_ID }) => {
  // Closure to maintain access to current state
  return () => {
    return !isResourcePending({ state: getState(), type, id });
  };
};

// The type has no LOADED nor PENDING requests
export const isResourceTypeUnloaded = ({ state, type }) => {
  const loaded = getResourceTypeByStatus({ state, type, status: resourceStatus.LOADED });
  const pending = getResourceTypeByStatus({ state, type, status: resourceStatus.PENDING });
  return !hasActiveResources({...loaded, ...pending});
};

// The type has no PENDING requests and has LOADED requests
export const isResourceTypeLoaded = ({ state, type }) => {
  const loaded = getResourceTypeByStatus({ state, type, status: resourceStatus.LOADED });
  const pending = getResourceTypeByStatus({ state, type, status: resourceStatus.PENDING });
  return !hasActiveResources(pending) && hasActiveResources(loaded);
};

// The type has any PENDING requests
export const isResourceTypePending = ({ state, type }) => {
  const pending = getResourceTypeByStatus({ state, type, status: resourceStatus.PENDING });
  return hasActiveResources(pending);
};

